/** Pure helpers for the Sources screen of the Visitors Desk. */
import type { SourceRow } from '../api/visitorsDesk'
import type { DeskFilter, FilterKey } from './deskFilters'
import type { Tone } from './deskFormat'

export interface SourceNode { row: SourceRow; children: SourceRow[] }

/** Channel rows with their sub-rows nested, keeping the API's order. A child
 *  whose parent is missing stands on its own rather than disappearing. */
export function sourceTree(rows: SourceRow[]): SourceNode[] {
  const keys = new Set(rows.map((r) => r.key))
  const nodes = new Map<string, SourceNode>()
  const out: SourceNode[] = []
  for (const r of rows) {
    if (r.parent === null || !keys.has(r.parent)) {
      const n = { row: r, children: [] }
      nodes.set(r.key, n)
      out.push(n)
    }
  }
  for (const r of rows) {
    if (r.parent !== null && nodes.has(r.parent)) nodes.get(r.parent)!.children.push(r)
  }
  return out
}

/** The pill a row sets. Keys look like `Search`, `Direct|home`, `Social|Facebook`,
 *  `Forums|-|rcgroups.com` and `Search|(other)`; the Other row has no value of its own. */
export function rowFilter(row: SourceRow): DeskFilter {
  const [channel, ...rest] = row.key.split('|')
  if (row.level === 'channel') return { key: 'channel', value: channel }
  if (row.level === 'host') return { key: 'referrer_host', value: row.key.split('|').slice(2).join('|') }
  if (rest.join('|') === '(other)') return { key: 'channel', value: channel }
  return { key: 'channel_detail', value: rest.join('|') }
}

/** Every pill a row sets: Direct sub-rows carry their split as `channel_detail`, host rows their channel. */
export function rowFilters(row: SourceRow): DeskFilter[] {
  const f = rowFilter(row)
  const [channel, detail] = row.key.split('|')
  if (row.level === 'channel' && channel === 'Direct' && detail) return [f, { key: 'channel_detail', value: detail }]
  if (row.level === 'host') return [{ key: 'channel', value: channel }, f]
  return [f]
}

export interface Crumb { label: string; remove: FilterKey[] }

/** Channel › host trail for the referrer drill. `remove` lists the filters a click on the crumb drops. */
export function drillCrumbs(filters: DeskFilter[], channelLabels: Record<string, string> = {}): Crumb[] {
  const host = filters.find((f) => f.key === 'referrer_host')
  if (!host) return []
  const out: Crumb[] = []
  const ch = filters.find((f) => f.key === 'channel')
  if (ch) out.push({ label: channelLabels[ch.value] ?? ch.value, remove: ['referrer_host', 'channel_detail'] })
  out.push({ label: host.value, remove: [] })
  return out
}

/** `▲ 3%`; a missing change (small base, today, no earlier window) is a dash. */
export function changePctText(pct: number | null | undefined): { text: string; tone: Tone } {
  if (pct === null || pct === undefined) return { text: '—', tone: 'flat' }
  const n = Math.round(pct * 100)
  if (n === 0) return { text: '0%', tone: 'flat' }
  return n > 0 ? { text: `▲ ${n}%`, tone: 'up' } : { text: `▼ ${Math.abs(n)}%`, tone: 'down' }
}
